/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';

import {
    TextButton,
    Text,
    Spacer,
    Alert,
    CloseButton,
    Heading,
    Tabs,
} from '@zillow/constellation';

import { Container, Col, Row } from '@zillow/zrs-comet';

// Constants
import { BILLING_PAGE } from 'app/shared/constants/RouteConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';

// Actions
import ModalActions from 'app/shared/actions/ModalActions';
import BillingActions from 'app/shared/actions/BillingActions';

// Components
import PermissionCheck from 'app/shared/components/PermissionCheck';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// Selectors
import { RoutingSelectors, UserSelectors, AppSelectors } from 'app/shared/selectors';

import { RMWContext } from 'app/shared/providers/RMWContext';

import PaymentDuePage from 'app/shared/containers/PaymentDuePage';
import PaidInvoicesPage from 'app/shared/containers/PaidInvoicesPage';
import PaymentSettingsPage from 'app/shared/containers/PaymentSettingsPage';
import SearchBox from 'app/shared/components/SearchBox';

import billingUtils from 'app/shared/utils/billingUtils';
import accessUtils from 'app/shared/utils/accessUtils';

import { StyledContainer } from './StyledComponents';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Billing';

const TabList = {
    paid: 'paid',
    due: 'due',
    paymentSettings: 'paymentSettings',
};

const propTypes = {
    router: PropTypes.object.isRequired,
};

const contextTypes = {
    paths: PropTypes.object,
};

const BillingPage = ({
    router,
    user,
    isScreenMobile,
    currentInvoiceLoadingState,
    displaySuccessMessage,
}) => {
    const { paths } = React.useContext(RMWContext);
    const dispatch = useDispatch();
    const paymentsEnabled = accessUtils.canAccessInvoicePayments({ user });


    React.useEffect(() => {
        return () => {
            dispatch(BillingActions.setDisplayPaymentSuccess(false));
            dispatch(BillingActions.initUserSettings());
        };
    }, []);


    const loadTab = (tab) => {
        const baseDetailsUrl = paths.billingPage;
        const url = `${baseDetailsUrl}/${tab}`;
        return window.router.transitionTo(url);
    };

    const getActiveTab = () => {
        // Expecting path to be [baseUrl]/rental-manager/billing/*something*
        const tabName = router.location.pathname.split('/')[3];

        switch (tabName) {
            case paths.billingDue:
                return paths.billingDue;
            case paths.billingPaid:
                return paths.billingPaid;
            case 'paymentSettings':
                return 'paymentSettings';
            default:
                return paths.billingDue;
        }
    };

    const getSectionUrl = (section) => {
        const baseDetailsUrl = paths.billingPage;
        return `${baseDetailsUrl}/${section}([?].*)?`;
    };

    const renderChildRoutes = () => {
        const baseDetailsUrl = paths.billingPage;
        const isOnlyNucleusUser = billingUtils.isOnlyNucleusUser(user);

        // child paths
        const childPathDue = getSectionUrl(paths.billingDue);
        const childPathPaid = getSectionUrl(paths.billingPaid);
        const childPathPaymentSettings = getSectionUrl('paymentSettings');
        return (
            <Switch>
                <Route
                    render={() => {
                        if (isOnlyNucleusUser) {
                            return <Redirect to={`${baseDetailsUrl}/${paths.billingPaid}`} />;
                        }
                        return <Route component={PaymentDuePage} path={childPathDue} />;
                    }}
                    path={childPathDue}
                />
                <Route component={PaidInvoicesPage} path={childPathPaid} />
                <Route component={PaymentSettingsPage} path={childPathPaymentSettings} />
            </Switch>
        );
    };

    const handlePayInfo = () => {
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.PAYMENT_INFO_MODAL,
        }));
    };

    const handleSearch = (searchTerm) => {
        dispatch(BillingActions.search(searchTerm));
    };

    const isSearchEnabled = !billingUtils.isOnlyNucleusUser(user);

    const renderPaymentInfo = () => {
        if (isScreenMobile) return null;
        return (
            <Spacer marginTop="xs">
                <Text textAlign="right" fontType="bodySmall">
                    *Online payments allows credit cards
                    {' '}
                    <TextButton onClick={handlePayInfo} fontType="bodySmall">How to Pay?</TextButton>
                </Text>
            </Spacer>
        );
    };

    const renderSubNav = () => (
        <Spacer paddingX="lg" marginY="xs">
            <Container>
                <Row>
                    <Col>
                        <Tabs>
                            <Tabs.List defaultSelected={getActiveTab()} onSelected={loadTab}>
                                <Tabs.Tab id={TabList.due}>
                                    Payment Due
                                </Tabs.Tab>
                                <Tabs.Tab id={TabList.paid}>
                                    History
                                </Tabs.Tab>
                                {paymentsEnabled && (
                                    <Tabs.Tab id={TabList.paymentSettings}>
                                        Settings
                                    </Tabs.Tab>
                                )}
                            </Tabs.List>
                        </Tabs>

                    </Col>
                </Row>
            </Container>
        </Spacer>
    );

    const renderContent = () => {
        const isOnlyNucleusUser = billingUtils.isOnlyNucleusUser(user);
        return (
            <PermissionCheck route={BILLING_PAGE}>
                <Container className="BillingPage-container">
                    {displaySuccessMessage && (
                        <Row>
                            <Col xs={12}>
                                <Alert
                                    isOpen={true}
                                    appearance="success"
                                    body="Thank you for your payment!"
                                    onClose={() => dispatch(BillingActions.setDisplayPaymentSuccess(false))}
                                    closeButton={<CloseButton />}
                                />
                            </Col>
                        </Row>
                    )}
                    <StyledContainer className="BillingPage-card">
                        <Row>
                            <Col md={7} sm={12}>
                                <Spacer paddingX="lg">
                                    <Heading level="2" fontFamily="serif">{DEFAULT_PAGE_TITLE}</Heading>
                                </Spacer>
                            </Col>
                            <Col md={5} sm={12}>
                                <Spacer paddingX="lg">
                                    {isSearchEnabled && (
                                        <>
                                            <SearchBox
                                                onSubmit={handleSearch}
                                                placeholder="Search by Invoice No"
                                                isSearching={currentInvoiceLoadingState === LoadingConstants.LOADING}
                                            />
                                            {paymentsEnabled && renderPaymentInfo()}
                                        </>
                                    )}
                                </Spacer>
                            </Col>
                        </Row>
                        {!isOnlyNucleusUser && renderSubNav()}
                        {renderChildRoutes()}
                    </StyledContainer>
                </Container>
            </PermissionCheck>
        );
    };


    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <div className="BillingPage">
                <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                {renderContent()}
            </div>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    router: RoutingSelectors.makeRouting(state),
    currentInvoiceLoadingState: state.billing.invoice.currentInvoiceLoadingState,
    user: UserSelectors.makeUser(state),
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    displaySuccessMessage: state.billing.payment.displaySuccessMessage,
});

BillingPage.contextTypes = contextTypes;
BillingPage.propTypes = propTypes;

export default withErrorBoundary(connect(mapStateToProps)(BillingPage));
